/**
 * New-build watch — tells an open window that a newer build has been deployed.
 *
 * The app is one CRA bundle with no service worker, and it is installable, so a
 * tab or an installed-app window that stays open keeps running the build it
 * loaded: moving between pages never fetches the bundle again. A page shipped
 * since then is simply not there, and the old code keeps calling a newer API.
 *
 * A build is its main bundle's file name (static/js/main.<hash>.js). The hash is
 * of the bundle's content, so it changes only when client code changes. Measured
 * on production 2026-10-12: eleven deployments in a row built main.55fa5a8e.js.
 * The first of them was a commit that changed src/; the ten after it changed
 * nothing under src/ (api/, scripts/, the log). The deployment before those
 * had built main.4c4cb963.js. So an API-only push never raises the notice.
 *   - the build this window runs: the <script> tag the page itself loaded
 *   - the build the server has now: /asset-manifest.json, written by the build
 * Different names mean a reload brings different code. That holds after a
 * rollback too, where the reload is just as wanted.
 *
 * The watch never reloads the page. It reports, and the notice
 * (src/components/NewVersionNotice.jsx) leaves the reload to a click: people are
 * mid-quote or mid-form, and a reload clears whatever is typed and not saved.
 *
 * It asks on an interval and when the window is looked at again, at most once
 * per MIN_GAP_MS, never while the window is hidden or offline. Every failure is
 * silent (offline, a timeout, an error page where the JSON should be) and the
 * next asking comes no sooner for it. Production builds only: the dev server's
 * bundle.js has no hash to compare.
 *
 * No imports, and everything it touches can be handed in: the notice and the
 * node tests (scripts/test_new_version_notice.js) both load this file.
 */

export const CHECK_EVERY_MS = 15 * 60 * 1000;
export const MIN_GAP_MS = 60 * 1000;
export const FETCH_TIMEOUT_MS = 10 * 1000;

const MAIN_BUNDLE = /(?:^|\/)static\/js\/(main\.[A-Za-z0-9_-]+\.js)(?:[?#].*)?$/;

/** "/static/js/main.55fa5a8e.js", or the same as a full URL → "main.55fa5a8e.js". */
export function mainBundleName(ref) {
  if (typeof ref !== "string") return null;
  const m = MAIN_BUNDLE.exec(ref.trim());
  return m ? m[1] : null;
}

/** The build this page is running: the main bundle its own <script> tag loaded. */
export function runningBuild(doc) {
  const scripts = doc && doc.scripts ? Array.from(doc.scripts) : [];
  for (const s of scripts) {
    const name = mainBundleName(s && (s.getAttribute ? s.getAttribute("src") : s.src));
    if (name) return name;
  }
  return null;
}

/** The build the server has now: the main bundle named in asset-manifest.json. */
export function buildInManifest(manifest) {
  if (!manifest || typeof manifest !== "object") return null;
  const files = manifest.files && typeof manifest.files === "object" ? manifest.files : null;
  const named = files ? mainBundleName(files["main.js"]) : null;
  if (named) return named;
  const entries = Array.isArray(manifest.entrypoints) ? manifest.entrypoints : [];
  for (const e of entries) {
    const name = mainBundleName(e);
    if (name) return name;
  }
  return null;
}

export function manifestUrl() {
  return (process.env.PUBLIC_URL || "") + "/asset-manifest.json";
}

/**
 * options (all optional; the defaults are the real page):
 *   win, doc    the window and the document
 *   fetch       (url, init) => Promise<Response>
 *   timers      { setInterval, clearInterval, setTimeout, clearTimeout }
 *   now         () => milliseconds
 *   enabled     true/false; default is "this is a production build"
 *   everyMs, minGapMs, timeoutMs
 */
export function createBuildWatch(options = {}) {
  const win = () => (options.win !== undefined ? options.win : typeof window !== "undefined" ? window : null);
  const doc = () => (options.doc !== undefined ? options.doc : typeof document !== "undefined" ? document : null);
  const timers = () => options.timers || win();
  const now = options.now || (() => Date.now());
  const enabled = () => (options.enabled !== undefined ? !!options.enabled : process.env.NODE_ENV === "production");
  const everyMs = options.everyMs != null ? options.everyMs : CHECK_EVERY_MS;
  const minGapMs = options.minGapMs != null ? options.minGapMs : MIN_GAP_MS;
  const timeoutMs = options.timeoutMs != null ? options.timeoutMs : FETCH_TIMEOUT_MS;

  let started = false;
  let running = null;    // the build this window loaded
  let latest = null;     // the build the server had at the last answer
  let dismissed = null;  // the offered build the notice was closed on
  let offered = null;    // what the notice shows: a build name, or null
  let lastAskedAt = 0;
  let asking = false;
  let intervalId = null;
  const listeners = new Set();

  function settle() {
    const next = running && latest && latest !== running && latest !== dismissed ? latest : null;
    if (next === offered) return;
    offered = next;
    listeners.forEach((l) => { try { l(); } catch { /* one listener must not silence the rest */ } });
  }

  function ask() {
    const w = win();
    const fetchFn = options.fetch || (w && typeof w.fetch === "function" ? (url, init) => w.fetch(url, init) : null);
    if (!fetchFn) return Promise.resolve(null);
    const t = timers();
    const ctl = typeof AbortController !== "undefined" ? new AbortController() : null;
    let timeoutId = null;
    // A request that never answers must not hold up every later one.
    const giveUp = new Promise((_, reject) => {
      timeoutId = t.setTimeout(() => { if (ctl) ctl.abort(); reject(new Error("timeout")); }, timeoutMs);
    });
    const answer = Promise.resolve()
      .then(() => fetchFn(manifestUrl(), { cache: "no-store", signal: ctl ? ctl.signal : undefined }))
      .then((res) => (res && res.ok ? res.json() : null))
      .then(buildInManifest);
    return Promise.race([answer, giveUp]).finally(() => t.clearTimeout(timeoutId));
  }

  /** Asks the server, unless it was asked less than minGapMs ago. Resolves true if it asked. */
  async function check() {
    try {
      if (!started || asking) return false;
      const at = now();
      const since = at - lastAskedAt;
      if (since >= 0 && since < minGapMs) return false; // since < 0: the clock was set back, which must not hold the next asking up
      const w = win();
      if (w && w.navigator && w.navigator.onLine === false) return false;
      lastAskedAt = at;
      asking = true;
      try {
        const name = await ask();
        if (name) { latest = name; settle(); }
      } finally {
        asking = false;
      }
      return true;
    } catch {
      return true; // asked and got nothing usable; whatever was known stays as it was
    }
  }

  function wake() {
    const d = doc();
    if (d && d.visibilityState === "hidden") return; // a hidden window waits until it is looked at again
    check();
  }

  function start() {
    if (started || !enabled()) return;
    const w = win();
    const d = doc();
    if (!w || !d) return;
    running = runningBuild(d);
    if (!running) return; // not a hashed production page: nothing to compare with
    started = true;
    lastAskedAt = now(); // the page has only just loaded this build
    intervalId = timers().setInterval(wake, everyMs);
    d.addEventListener("visibilitychange", wake);
    w.addEventListener("focus", wake);
    w.addEventListener("pageshow", wake);
    w.addEventListener("online", wake);
  }

  function stop() {
    if (!started) return;
    started = false;
    timers().clearInterval(intervalId);
    intervalId = null;
    const w = win();
    const d = doc();
    if (d) d.removeEventListener("visibilitychange", wake);
    if (w) {
      w.removeEventListener("focus", wake);
      w.removeEventListener("pageshow", wake);
      w.removeEventListener("online", wake);
    }
  }

  return {
    // For useSyncExternalStore. The first subscriber starts the watch, so a
    // screen that never mounts the notice never asks the server.
    subscribe(listener) {
      listeners.add(listener);
      start();
      return () => { listeners.delete(listener); };
    },
    getSnapshot: () => offered,
    check,
    start,
    stop,
    now,
    /** Hides the notice until the server has yet another build. */
    dismiss() { dismissed = latest; settle(); },
    /** Only ever called from a click on the notice's Reload button. */
    reload() {
      const w = win();
      if (w && w.location && typeof w.location.reload === "function") w.location.reload();
    },
    state: () => ({ started, running, latest, dismissed, offered, lastAskedAt }),
  };
}

// The page's one watch.
export const buildWatch = createBuildWatch();
